/**
 * Client skin registry — keep ids/fills in sync with aws-blocks/lib/player-colors.ts
 */
import type { CSSProperties } from 'react';

/** How player names / turn banners render for this skin. */
export type SkinLabelStyle = {
  /** Solid text color (defaults to skin.fill). Ignored when `gradient` is set. */
  color?: string;
  /** Multi-stop colors for gradient text (left → right). */
  gradient?: string[];
  /** Outline around glyphs for contrast on busy backgrounds. */
  stroke?: string;
  /** CSS length for outline width (e.g. `1px`). */
  strokeWidth?: string;
};

export type SkinRarity = 'common' | 'uncommon' | 'rare' | 'legendary';

export type PlayerSkinDef = {
  id: string;
  label: string;
  fill: string;
  stroke: string;
  isDefault?: boolean;
  defaultTeamIndex?: 0 | 1;
  /** Linked sprite pack for piece art / frame animations */
  spritePackId?: string;
  rarity?: SkinRarity;
  /** Optional banner / nameplate styling (gradients, custom color, outline). */
  labelStyle?: SkinLabelStyle;
};

export const PLAYER_SKINS: PlayerSkinDef[] = [
  {
    id: 'color-slate',
    label: 'Black',
    fill: '#2a2a2e',
    stroke: '#f0ece4',
    isDefault: true,
    defaultTeamIndex: 0,
    spritePackId: 'classic',
    rarity: 'common',
  },
  {
    id: 'color-crimson',
    label: 'Red',
    fill: '#ef4444',
    stroke: '#991b1b',
    isDefault: true,
    defaultTeamIndex: 1,
    spritePackId: 'classic',
    rarity: 'common',
  },
  {
    id: 'color-azure',
    label: 'Azure',
    fill: '#38bdf8',
    stroke: '#0369a1',
    spritePackId: 'classic',
    rarity: 'uncommon',
  },
  {
    id: 'color-emerald',
    label: 'Emerald',
    fill: '#34d399',
    stroke: '#047857',
    spritePackId: 'classic',
    rarity: 'uncommon',
  },
  {
    id: 'color-indigo',
    label: 'Indigo',
    fill: '#6366f1',
    stroke: '#312e81',
    spritePackId: 'classic',
    rarity: 'uncommon',
  },
  {
    id: 'color-coral',
    label: 'Coral',
    fill: '#fb923c',
    stroke: '#c2410c',
    spritePackId: 'classic',
    rarity: 'uncommon',
  },
  {
    id: 'color-mint',
    label: 'Mint',
    fill: '#5eead4',
    stroke: '#0f766e',
    spritePackId: 'classic',
    rarity: 'uncommon',
  },
  {
    id: 'color-sand',
    label: 'Sand',
    fill: '#e7c68c',
    stroke: '#92400e',
    spritePackId: 'classic',
    rarity: 'uncommon',
  },
  {
    id: 'color-violet',
    label: 'Violet',
    fill: '#a78bfa',
    stroke: '#6d28d9',
    spritePackId: 'classic',
    rarity: 'rare',
  },
  {
    id: 'color-amber',
    label: 'Amber',
    fill: '#fbbf24',
    stroke: '#b45309',
    spritePackId: 'classic',
    rarity: 'rare',
  },
  {
    id: 'color-cyan',
    label: 'Cyan',
    fill: '#22d3ee',
    stroke: '#0e7490',
    spritePackId: 'classic',
    rarity: 'rare',
  },
  {
    id: 'color-rose',
    label: 'Rose',
    fill: '#fb7185',
    stroke: '#be123c',
    spritePackId: 'classic',
    rarity: 'rare',
  },
  {
    id: 'color-magenta',
    label: 'Magenta',
    fill: '#e879f9',
    stroke: '#a21caf',
    spritePackId: 'classic',
    rarity: 'rare',
  },
  {
    id: 'color-obsidian',
    label: 'Obsidian',
    fill: '#0f172a',
    stroke: '#c9a227',
    spritePackId: 'classic',
    rarity: 'rare',
  },
  {
    id: 'color-ivory',
    label: 'Ivory',
    fill: '#f5f0e6',
    stroke: '#78716c',
    spritePackId: 'classic',
    rarity: 'rare',
  },
  {
    id: 'skin-realm',
    label: 'Realm',
    fill: '#8B939C',
    stroke: '#2A9D8F',
    spritePackId: 'realm',
    rarity: 'legendary',
    labelStyle: {
      gradient: ['#C5CBD1', '#2A9D8F', '#F2E9D8'],
      stroke: '#0a0e17',
      strokeWidth: '1.15px',
    },
  },
];

export const UNTURNED_COLOR = { fill: '#4ade80', stroke: '#166534' } as const;

const byId = new Map(PLAYER_SKINS.map((c) => [c.id, c]));

export function getPlayerSkinDef(skinId?: string | null): PlayerSkinDef {
  if (skinId && byId.has(skinId)) return byId.get(skinId)!;
  return PLAYER_SKINS.find((c) => c.defaultTeamIndex === 0) ?? PLAYER_SKINS[0];
}

export function getDefaultSkinIdForTeam(teamIndex: 0 | 1): string {
  return (
    PLAYER_SKINS.find((c) => c.defaultTeamIndex === teamIndex)?.id ??
    PLAYER_SKINS[0].id
  );
}

/** CSS for player names / turn banners — supports solid, gradient, and outline. */
export function skinLabelStyle(skin: PlayerSkinDef): CSSProperties {
  const label = skin.labelStyle;
  const outline = label?.stroke ?? 'rgba(255, 255, 255, 0.85)';
  const outlineWidth = label?.strokeWidth ?? '0.4px';
  const outlineCss: CSSProperties = {
    WebkitTextStroke: `${outlineWidth} ${outline}`,
    paintOrder: 'stroke fill',
    textShadow: `0 0 1px ${outline}`,
  };

  if (label?.gradient && label.gradient.length > 0) {
    const stops =
      label.gradient.length === 1
        ? [label.gradient[0], label.gradient[0]]
        : label.gradient;
    return {
      ...outlineCss,
      backgroundImage: `linear-gradient(105deg, ${stops.join(', ')})`,
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      color: 'transparent',
      WebkitTextFillColor: 'transparent',
    };
  }

  return {
    ...outlineCss,
    color: label?.color ?? skin.fill,
  };
}

/** Background preview for store/settings tiles (solid or gradient). */
export function skinSwatchBackground(skin: PlayerSkinDef): string {
  const g = skin.labelStyle?.gradient;
  if (g && g.length > 0) {
    const stops = g.length === 1 ? [g[0], g[0]] : g;
    return `linear-gradient(135deg, ${stops.join(', ')})`;
  }
  return skin.fill;
}

export type TeamPalette = {
  fill: string;
  stroke: string;
  colorId: string;
  spritePackId: string;
};

export function resolveTeamPalettes(
  firstColorId?: string | null,
  secondColorId?: string | null
): Record<0 | 1, TeamPalette> {
  const first = getPlayerSkinDef(firstColorId ?? getDefaultSkinIdForTeam(0));
  let second = getPlayerSkinDef(secondColorId ?? getDefaultSkinIdForTeam(1));
  if (second.id === first.id) {
    second = getPlayerSkinDef(
      getDefaultSkinIdForTeam(first.defaultTeamIndex === 0 ? 1 : 0)
    );
  }
  return {
    0: {
      fill: first.fill,
      stroke: first.stroke,
      colorId: first.id,
      spritePackId: first.spritePackId ?? 'classic',
    },
    1: {
      fill: second.fill,
      stroke: second.stroke,
      colorId: second.id,
      spritePackId: second.spritePackId ?? 'classic',
    },
  };
}

export function listPlayerSkins() {
  return PLAYER_SKINS;
}
